import { checkCrossCompanyOverlap } from "@lhd/hooks";
import { LOG_TYPE } from "@lhd/features/TrackingTime/HomeTracking/contants";
import { buildTrackingTimelineRows } from "@lhd/features/TrackingTime/timelineRows";
import { toLoggedAtIso } from "@lhd/features/TrackingTime/DetailTracking/buildSaveFormData";
import { LogItem, LogTimeByDateResponse } from "@lhd/types";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

const WORK_LOG_TIMEZONE = "Asia/Tokyo";
const LOGGED_AT_API_FORMAT = "YYYY-MM-DDTHH:mm:ssZ";

export class CrossCompanyOverlapError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CrossCompanyOverlapError";
  }
}

type LogtimePatch = {
  logged_at?: unknown;
};

const resolveLoggedAtForOverlap = (
  row: LogItem,
  formValues: Record<string, unknown>,
): string | null => {
  const patch = formValues[String(row.id)] as LogtimePatch | undefined;
  if (patch != null && typeof patch === "object" && !Array.isArray(patch)) {
    const fromForm = toLoggedAtIso(patch.logged_at);
    if (fromForm) return fromForm;
    if ("logged_at" in patch) return null;
  }
  const server =
    typeof row.logged_at === "string" && row.logged_at.trim()
      ? row.logged_at.trim()
      : "";
  return server.length > 0 ? server : null;
};

const endOfWorkDateCheckoutIso = (workDate: string): string =>
  dayjs
    .tz(workDate.trim().replace(/\//g, "-"), "YYYY-MM-DD", WORK_LOG_TIMEZONE)
    .endOf("day")
    .format(LOGGED_AT_API_FORMAT);

const toComparableMs = (iso: string): number | null => {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

export type CrossCompanyOverlapInterval = {
  checkin_logged_at: string;
  checkout_logged_at: string;
};

export function collectCrossCompanyOverlapIntervals(
  data: LogTimeByDateResponse,
  formValues: Record<string, unknown>,
  workDate: string,
): CrossCompanyOverlapInterval[] {
  const rows = buildTrackingTimelineRows(data);

  let earliestCheckin: string | null = null;
  let earliestCheckinMs = Infinity;
  let latestCheckout: string | null = null;
  let latestCheckoutMs = -Infinity;

  for (const row of rows) {
    if (row.log_type === LOG_TYPE.CHECKIN) {
      const loggedAt = resolveLoggedAtForOverlap(row, formValues);
      if (!loggedAt) continue;
      const ms = toComparableMs(loggedAt);
      if (ms != null && ms < earliestCheckinMs) {
        earliestCheckinMs = ms;
        earliestCheckin = loggedAt;
      }
      continue;
    }

    if (row.log_type === LOG_TYPE.CHECKOUT) {
      const loggedAt = resolveLoggedAtForOverlap(row, formValues);
      if (!loggedAt) continue;
      const ms = toComparableMs(loggedAt);
      if (ms != null && ms > latestCheckoutMs) {
        latestCheckoutMs = ms;
        latestCheckout = loggedAt;
      }
    }
  }

  if (!earliestCheckin) return [];

  return [
    {
      checkin_logged_at: earliestCheckin,
      checkout_logged_at: latestCheckout ?? endOfWorkDateCheckoutIso(workDate),
    },
  ];
}

export async function validateCrossCompanyOverlapBeforeSave(options: {
  companyId: string;
  workerId: number;
  workDate: string;
  data: LogTimeByDateResponse;
  formValues: Record<string, unknown>;
}): Promise<void> {
  const { companyId, workerId, workDate, data, formValues } = options;
  const intervals = collectCrossCompanyOverlapIntervals(
    data,
    formValues,
    workDate,
  );

  for (const interval of intervals) {
    const result = await checkCrossCompanyOverlap({
      company_id: companyId,
      worker_id: workerId,
      date: workDate,
      checkin_logged_at: interval.checkin_logged_at,
      checkout_logged_at: interval.checkout_logged_at,
    });

    if (result.has_overlap) {
      const message =
        (typeof result.msg === "string" && result.msg.trim()) ||
        "他の会社と勤務時間が重複しているため、保存できません。";
      throw new CrossCompanyOverlapError(message);
    }
  }
}
